import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { API } from "@/config";
import { useLanguage } from "@/i18n/LanguageContext";

export default function PaymentStatus() {
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const cancelled = searchParams.get("cancelled");
  const isRazorpay = searchParams.get("gateway") === "razorpay";
  const [state, setState] = useState(
    isRazorpay ? "success" : cancelled || !sessionId ? "failed" : "checking"
  );
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId || cancelled) return;
    let timer;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState("success");
          return;
        }
      } catch (err) {
        console.error("Payment status check failed:", err);
      }
      attempts.current += 1;
      if (attempts.current < 8) {
        timer = setTimeout(poll, 2000);
      } else {
        setState("failed");
      }
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId, cancelled]);

  return (
    <div
      data-testid="payment-status-page"
      className="flex min-h-screen items-center justify-center bg-background px-4"
    >
      <div className="w-full max-w-md rounded-2xl border border-border bg-card p-10 text-center shadow-sm">
        {state === "checking" && (
          <>
            <Loader2 className="mx-auto h-14 w-14 animate-spin text-amber-500" />
            <p data-testid="payment-checking-message" className="mt-6 text-base font-medium">
              {t.payment.checking}
            </p>
          </>
        )}
        {state === "success" && (
          <>
            <CheckCircle2 className="mx-auto h-14 w-14 text-emerald-500" />
            <h1 data-testid="payment-success-message" className="mt-6 font-heading text-2xl font-bold">
              {t.payment.successTitle}
            </h1>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{t.payment.successMsg}</p>
          </>
        )}
        {state === "failed" && (
          <>
            <XCircle className="mx-auto h-14 w-14 text-destructive" />
            <p data-testid="payment-failed-message" className="mt-6 text-base font-medium">
              {t.payment.failed}
            </p>
          </>
        )}
        {state !== "checking" && (
          <Button asChild className="mt-8" data-testid="payment-back-home-button">
            <Link to="/">{t.payment.backHome}</Link>
          </Button>
        )}
      </div>
    </div>
  );
}
